import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, ShieldAlert, Rocket, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SettingsTabs } from "@/components/SettingsTabs";
import { BILLING } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const PLAN_LABEL = {
  sandbox: "Sandbox", pro_trial: "Pro Trial", pro: "Pro", business: "Business", enterprise: "Enterprise",
};

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(window.Razorpay);
    let script = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = RAZORPAY_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.Razorpay));
    script.addEventListener("error", () => reject(new Error("Could not load Razorpay checkout")));
  });
}

function UsageBar({ label, used, limit, testid }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = limit && used >= limit;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={over ? "text-red-400" : "text-foreground"}>{used}{limit ? ` / ${limit}` : ""}</span>
      </div>
      {limit ? (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className={`h-full rounded-full ${over ? "bg-red-500" : "bg-accent"}`} style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <div className="mt-1.5 text-[10px] uppercase tracking-[0.14em] text-accent">unlimited</div>
      )}
    </div>
  );
}

export default function SettingsBilling() {
  const { active } = useWorkspace();
  const canManage = active && (active.role === "owner" || active.role === "admin");
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [seats, setSeats] = useState(3);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!active) return;
    const { data: res } = await api.get("/billing", { params: { workspace_id: active.id } });
    setData(res);
  }, [active]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRazorpay().catch(() => {}); }, []);

  const startTrial = async () => {
    setBusy("trial");
    try {
      await api.post("/billing/start-trial", { workspace_id: active.id });
      toast.success("14-day Pro trial started");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start trial");
    } finally { setBusy(""); }
  };

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const [{ data: res }, Razorpay] = await Promise.all([
        api.post("/billing/checkout", { workspace_id: active.id, plan, period, seats: Number(seats) }),
        loadRazorpay(),
      ]);
      const rzp = new Razorpay({
        key: res.key_id,
        subscription_id: res.subscription_id,
        name: "Sema",
        description: `${PLAN_LABEL[res.plan]} · ${res.seats} seats · billed ${res.period} ($${res.amount})`,
        prefill: res.prefill,
        notes: { workspace_id: active.id },
        theme: { color: "#0fae96", backdrop_color: "rgba(9,13,18,0.85)" },
        modal: { ondismiss: () => setBusy("") },
        handler: async (rsp) => {
          try {
            await api.post("/billing/verify", {
              workspace_id: active.id,
              razorpay_payment_id: rsp.razorpay_payment_id,
              razorpay_subscription_id: rsp.razorpay_subscription_id,
              razorpay_signature: rsp.razorpay_signature,
            });
            toast.success(`Payment confirmed — welcome to ${PLAN_LABEL[res.plan]}!`);
            await load();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed — please contact support.");
          } finally { setBusy(""); }
        },
      });
      rzp.on("payment.failed", (rsp) => {
        toast.error(rsp?.error?.description || "Payment failed — please try again.");
      });
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout failed");
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel", { workspace_id: active.id });
      toast.success("Subscription cancelled — reverted to Sandbox");
      await load();
    } catch { toast.error("Could not cancel"); } finally { setBusy(""); }
  };

  const proPrice = period === "annual" ? data?.prices?.pro_annual : data?.prices?.pro_monthly;
  const bizPrice = period === "annual" ? data?.prices?.business_annual : data?.prices?.business_monthly;
  const isSandbox = data?.plan === "sandbox";
  const isPaid = data?.plan === "pro" || data?.plan === "business";

  return (
    <div data-testid={BILLING.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-4xl px-6 py-10">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Billing &amp; plan</h1>
        <p className="mt-2 text-sm text-muted-foreground">Manage your plan, seats, and usage for <span className="text-foreground">{active?.name}</span>.</p>
        <SettingsTabs />

        {!canManage ? (
          <div className="mt-10 rounded-xl border border-border/60 bg-card/40 p-8 text-center text-sm text-muted-foreground">
            <ShieldAlert className="mx-auto mb-3 h-6 w-6 text-amber-400" />
            Billing requires an admin or owner role.
          </div>
        ) : !data ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : (
          <>
            {/* current plan + usage */}
            <section className="mt-8 grid gap-5 md:grid-cols-2">
              <div className="rounded-xl border border-border/60 bg-card/40 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold">Current plan</h2>
                  <span data-testid={BILLING.planBadge} className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-accent">
                    <Sparkles className="h-3 w-3" />{PLAN_LABEL[data.plan] || data.plan}
                  </span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  {data.stored_plan === "pro_trial" && data.days_left != null
                    ? <>Trial — <span className="text-foreground">{data.days_left} day{data.days_left === 1 ? "" : "s"} left</span>.</>
                    : isPaid ? <>{data.seats} seats · {data.seats_used} used · auto-renews via Razorpay.</>
                    : <>{data.seats_used} member{data.seats_used === 1 ? "" : "s"} in this workspace.</>}
                </p>
                {isSandbox && (
                  <Button onClick={startTrial} disabled={busy === "trial"} data-testid={BILLING.startTrial} className="mt-4 rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90">
                    {busy === "trial" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="mr-2 h-4 w-4" />Start 14-day Pro trial</>}
                  </Button>
                )}
                {(isPaid || data.stored_plan === "pro_trial") && (
                  <Button variant="outline" onClick={cancel} disabled={busy === "cancel"} data-testid={BILLING.cancelPlan} className="mt-4 rounded-full border-border/80 text-xs uppercase tracking-[0.16em]">
                    {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel / downgrade"}
                  </Button>
                )}
              </div>

              <div className="rounded-xl border border-border/60 bg-card/40 p-5">
                <h2 className="text-base font-semibold">This month's usage</h2>
                <div className="mt-4 space-y-4">
                  <UsageBar label="Chat queries" used={data.usage.chat} limit={data.limits?.chat_per_month} testid={BILLING.usageChat} />
                  <UsageBar label="Saved reports" used={data.usage.reports} limit={data.limits?.saved_reports} testid={BILLING.usageReports} />
                  <UsageBar label="Connectors" used={data.usage.connectors} limit={data.limits?.connectors} testid={BILLING.usageConnectors} />
                </div>
              </div>
            </section>

            {/* upgrade */}
            <section className="mt-6 rounded-xl border border-border/60 bg-card/40 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-base font-semibold">{isPaid ? "Change plan" : "Upgrade"}</h2>
                <div className="inline-flex items-center rounded-full border border-border/60 bg-background/60 p-1 text-[11px] uppercase tracking-[0.16em]">
                  <button data-testid={BILLING.togglePeriodMonthly} onClick={() => setPeriod("monthly")} className={`rounded-full px-3 py-1 ${period === "monthly" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>Monthly</button>
                  <button data-testid={BILLING.togglePeriodAnnual} onClick={() => setPeriod("annual")} className={`rounded-full px-3 py-1 ${period === "annual" ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`}>Annual −20%</button>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Seats</Label>
                <Input type="number" min={3} value={seats} onChange={(e) => setSeats(e.target.value)} data-testid={BILLING.seatsInput} className="h-9 w-24 bg-background/60 text-sm" />
                <span className="text-xs text-muted-foreground">Pro min 3 · Business min 10</span>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[
                  { key: "pro", name: "Pro", price: proPrice, testid: BILLING.upgradePro, perks: ["Unlimited chat + connectors", "Reports, alerts, signals", "RBAC + audit log"] },
                  { key: "business", name: "Business", price: bizPrice, testid: BILLING.upgradeBusiness, perks: ["Everything in Pro", "SSO + advanced governance", "Priority support · 99.9% SLA"] },
                ].map((p) => (
                  <article key={p.key} className="flex flex-col rounded-lg border border-border/60 bg-background/40 p-4">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-semibold">${p.price}</span>
                      <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">/user/mo</span>
                    </div>
                    <h3 className="mt-1 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">{p.name}</h3>
                    <ul className="my-3 space-y-1.5">
                      {p.perks.map((b) => (<li key={b} className="flex items-start gap-2 text-[12px] text-foreground/90"><Check className="mt-0.5 h-3.5 w-3.5 text-accent" />{b}</li>))}
                    </ul>
                    <Button onClick={() => checkout(p.key)} disabled={busy === p.key} data-testid={p.testid} className="mt-auto rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90">
                      {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="mr-2 h-4 w-4" />Choose {p.name}</>}
                    </Button>
                  </article>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-muted-foreground">
                Secure checkout via Razorpay — cards, UPI, netbanking &amp; wallets. Auto-renewing subscription, charged in USD. Amounts are computed server-side from seats × plan.
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
